import type { HtmlLegendItem } from '../chart-legend/chartjs-plugin-html-legend';

/** Builds a new canvas = chart canvas + a hand-drawn legend block appended
 *  below it, so the PNG export includes the legend without shrinking the
 *  plotted chart area (the legend lives in real Angular DOM on-screen via
 *  HtmlLegendPlugin/ChartLegendComponent, which is off-canvas and so
 *  invisible to canvas.toDataURL() on its own). Hidden datasets are
 *  skipped — they aren't plotted either, so listing them would mismatch
 *  what the exported chart actually shows. */
export function composeChartWithLegend(chartElement: HTMLCanvasElement, legendItems: HtmlLegendItem[]): HTMLCanvasElement {
  const items = legendItems.filter(item => !item.hidden);
  if (!items.length) return chartElement;

  const dpr = window.devicePixelRatio || 1;
  const width = chartElement.width;
  const padding = 12 * dpr;
  const swatchSize = 12 * dpr;
  const gapAfterSwatch = 6 * dpr;
  const rowGap = 8 * dpr;
  const colGap = 16 * dpr;
  const fontSize = 13 * dpr;
  const font = `${fontSize}px sans-serif`;

  // Measure to lay out items in wrapped rows, mirroring the on-screen flex-wrap legend.
  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d')!;
  measureCtx.font = font;

  const rows: { item: HtmlLegendItem; itemWidth: number }[][] = [[]];
  let rowWidth = 0;
  const maxRowWidth = width - padding * 2;

  items.forEach(item => {
    const textWidth = measureCtx.measureText(item.text).width;
    const itemWidth = swatchSize + gapAfterSwatch + textWidth;
    if (rowWidth + itemWidth > maxRowWidth && rows[rows.length - 1].length) {
      rows.push([]);
      rowWidth = 0;
    }
    rows[rows.length - 1].push({ item, itemWidth });
    rowWidth += itemWidth + colGap;
  });

  const rowHeight = Math.max(swatchSize, fontSize) + rowGap;
  const legendHeight = padding * 2 + rows.length * rowHeight - rowGap;

  const composed = document.createElement('canvas');
  composed.width = width;
  composed.height = chartElement.height + legendHeight;
  const ctx = composed.getContext('2d')!;

  // Chart canvas itself has no explicit background (transparent), so this
  // stays transparent too — only the drawImage below and the legend text
  // paint pixels, same as before this change.
  ctx.drawImage(chartElement, 0, 0);

  ctx.font = font;
  ctx.textBaseline = 'middle';

  let y = chartElement.height + padding + rowHeight / 2;
  rows.forEach(row => {
    let x = padding;
    row.forEach(({ item, itemWidth }) => {
      ctx.fillStyle = item.fillStyle;
      ctx.strokeStyle = item.strokeStyle;
      ctx.fillRect(x, y - swatchSize / 2, swatchSize, swatchSize);
      ctx.strokeRect(x, y - swatchSize / 2, swatchSize, swatchSize);

      ctx.fillStyle = '#334155'; // surface-700, matches ChartLegendComponent's non-hidden label colour
      ctx.fillText(item.text, x + swatchSize + gapAfterSwatch, y);

      x += itemWidth + colGap;
    });
    y += rowHeight;
  });

  return composed;
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

export function downloadTextFile(content: string, filename: string, mime = 'text/csv;charset=utf-8;'): void {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  downloadDataUrl(url, filename);
  URL.revokeObjectURL(url);
}
